import { useEffect, useState } from 'react';

/**
 * Сколько опрос ИИ-статуса может идти без перемены, прежде чем экран
 * сдастся: прогон, который полчаса «в очереди» или «в работе», завис, и
 * опрашивать его раз в несколько секунд дальше — только нагружать сервер.
 */
export const AI_POLL_GUARD_MS = 30 * 60 * 1000;

export interface IAiPollGuard {
  /** Опрашивать: статус активен и ещё не завис. */
  polling: boolean;
  /**
   * Статус активен непрерывно дольше `AI_POLL_GUARD_MS` (видимого времени):
   * опрос остановлен, экран говорит «trwa dłużej niż zwykle».
   */
  stalled: boolean;
}

/**
 * ТЗ 0104 — предохранитель опроса зависшего прогона. `active` — статус
 * «в движении» (очередь / работа), `key` — что именно опрашивается (id
 * прогона, черновика, разбора; у списка — набор активных строк): новый
 * ключ — новый отсчёт, статус сошёл с активного — отсчёт сброшен.
 *
 * Считается только время видимой вкладки: пока она скрыта, опроса нет
 * (`useConditionalPolling`), и зависанием это не считается — иначе,
 * вернувшись через час, сотрудник видел бы старое «пишется» без опроса.
 *
 * Ручное «Odśwież» (`refetch`) не снимает остановку, пока статус тот же:
 * это всё ещё тот же зависший прогон.
 */
export const useAiPollGuard = (
  active: boolean,
  key: string,
  limitMs: number = AI_POLL_GUARD_MS,
): IAiPollGuard => {
  const [stalledKey, setStalledKey] = useState<string | null>(null);

  useEffect(() => {
    if (!active) {
      setStalledKey(null);
      return undefined;
    }
    let remaining = limitMs;
    let armedAt = 0;
    let timer: number | undefined;
    const arm = () => {
      if (timer !== undefined) return;
      armedAt = Date.now();
      timer = window.setTimeout(() => setStalledKey(key), remaining);
    };
    const disarm = () => {
      if (timer === undefined) return;
      window.clearTimeout(timer);
      timer = undefined;
      remaining = Math.max(0, remaining - (Date.now() - armedAt));
    };
    const sync = () => {
      if (document.visibilityState === 'visible') arm();
      else disarm();
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      disarm();
    };
  }, [active, key, limitMs]);

  const stalled = active && stalledKey === key;
  return { polling: active && !stalled, stalled };
};
